import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, X, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  getStageData,
  saveStageData,
  completeStage,
  getAvailableUsers,
  getClaimTeam,
  assignTeamMember,
  removeTeamMember
} from '../../../api/claims';

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner', description: 'Primary responsible for the claim' },
  { value: 'watcher', label: 'Watcher', description: 'Receives notifications' },
  { value: 'support', label: 'Support', description: 'Provides support' },
  { value: 'finance_reviewer', label: 'Finance Reviewer', description: 'Reviews financial aspects' }
];

const TeamDefinitionModal = ({ open, onOpenChange, claimId, onComplete }) => {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('watcher');

  // Fetch stage data
  const { data: stageDataResponse, isLoading: stageLoading } = useQuery({
    queryKey: ['stageData', claimId, 'team_definition'],
    queryFn: () => getStageData(claimId, 'team_definition'),
    enabled: open && !!claimId
  });

  // Fetch available users
  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ['availableUsers', claimId],
    queryFn: () => getAvailableUsers(claimId),
    enabled: open && !!claimId
  });

  // Fetch current team
  const { data: teamResponse, isLoading: teamLoading } = useQuery({
    queryKey: ['claimTeam', claimId],
    queryFn: () => getClaimTeam(claimId),
    enabled: open && !!claimId
  });

  const stageData = stageDataResponse?.data || {};
  const availableUsers = usersResponse?.data || [];
  const teamMembers = teamResponse?.data || [];

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: ({ userId, role }) => assignTeamMember(claimId, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries(['claimTeam', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      setSelectedUserId('');
      setSelectedRole('watcher');
    }
  });

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: (assignmentId) => removeTeamMember(claimId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['claimTeam', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
    }
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: () => completeStage(claimId, 'team_definition'),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      if (onComplete) onComplete();
      onOpenChange(false);
    }
  });

  const handleAssign = () => {
    if (selectedUserId && selectedRole) {
      assignMutation.mutate({ userId: selectedUserId, role: selectedRole });
    }
  };

  const handleRemove = (assignmentId) => {
    removeMutation.mutate(assignmentId);
  };

  const handleComplete = () => {
    completeMutation.mutate();
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-blue-100 text-blue-800',
      watcher: 'bg-gray-100 text-gray-800',
      support: 'bg-green-100 text-green-800',
      finance_reviewer: 'bg-purple-100 text-purple-800'
    };
    return colors[role] || colors.watcher;
  };

  const isLoading = stageLoading || usersLoading || teamLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Team Definition</span>
            {stageData.is_completed && (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            )}
          </DialogTitle>
          <DialogDescription>
            Assign team members and define their roles for this claim.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Current Team Members */}
            {teamMembers.length > 0 && (
              <div className="space-y-3">
                <Label>Current Team Members</Label>
                <div className="space-y-2">
                  {teamMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                          {member.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{member.user_name}</p>
                          <p className="text-xs text-gray-500">{member.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getRoleBadgeColor(member.role)}>
                          {ROLE_OPTIONS.find(r => r.value === member.role)?.label || member.role}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(member.id)}
                          disabled={removeMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Team Member */}
            {!stageData.is_completed && (
              <div className="space-y-4 pt-4 border-t">
                <Label>Add Team Member</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="user-select" className="text-sm">User</Label>
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger id="user-select">
                        <SelectValue placeholder="Select user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role-select" className="text-sm">Role</Label>
                    <Select value={selectedRole} onValueChange={setSelectedRole}>
                      <SelectTrigger id="role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            <div>
                              <div className="font-medium">{role.label}</div>
                              <div className="text-xs text-gray-500">{role.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={handleAssign}
                  disabled={!selectedUserId || assignMutation.isPending}
                  className="w-full"
                >
                  {assignMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add to Team
                    </>
                  )}
                </Button>
              </div>
            )}

            {stageData.is_completed && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  This stage was completed on {new Date(stageData.completed_at).toLocaleDateString()}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {!stageData.is_completed && teamMembers.length > 0 && (
            <Button
              onClick={handleComplete}
              disabled={completeMutation.isPending}
            >
              {completeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Completing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeamDefinitionModal;
